/**
 * Makro-Brief — der tägliche „große Blick" mit Gedächtnis.
 *
 * User-Direktive (Ronnie, 03.07.2026): Der KI-Prompt ist einer der wichtigsten
 * Punkte der App. Es geht NICHT um heute — es geht um die Entwicklung über
 * Wochen und Monate. „Trump sagt heute Krieg zu Ende, morgen wieder nicht" —
 * Tagesrauschen darf das Bild nicht kippen. Drei Bausteine:
 *
 *  1. Verdichtete ZEITREIHEN statt Tageswerte (F&G-/VIX-Verlauf, Radar-Stände
 *     mit 13/26-Wochen-Fenstern — die tragen die Monats-Entwicklung in sich).
 *  2. GEDÄCHTNIS: Die KI sieht ihre eigenen letzten Urteile und muss sagen,
 *     was sich verfestigt, abgeschwächt oder gedreht hat.
 *  3. ANTI-RAUSCHEN-REGEL: Einzelmeldungen zählen nur, wenn sie einen
 *     mehrwöchigen Trend bestätigen oder brechen — sonst Ablage „Tagesrauschen".
 *
 * Leitplanke: KI erklärt und schätzt Wahrscheinlichkeiten — die gemessenen
 * Geldflüsse sind der Anker und gewinnen bei Widerspruch. Kein Score-Einfluss,
 * keine persönliche Anlageberatung. Reine Funktionen, voll testbar.
 */

export interface FlowPotBrief {
  label: string;
  rs13: number | null;
  rs26: number | null;
  hot?: boolean;
}

export interface MacroDossier {
  /** Heutiges Datum (YYYY-MM-DD). */
  datum: string;
  wetter: {
    phase: string;
    stance: string;
    greed_level: number;
    headline: string;
  } | null;
  markt_phase: string | null;
  kapitulation_score: number | null;
  /** #40 (14.07.) — Kreditmarkt-Frühindikatoren aus FRED (v5_macro_regime):
   *  Der Kreditmarkt riecht Stress Wochen vor dem Aktienmarkt. */
  kredit: { hy_spread_pct: number | null; zinskurve_10y2y: number | null } | null;
  /** Verlauf ältester → neuester Wert (heute, ~1 W, ~4 W, ~13 W zurück). */
  fg_verlauf: Array<{ datum: string; wert: number }>;
  vix_verlauf: Array<{ datum: string; wert: number }>;
  radar: {
    frueh: FlowPotBrief[];
    zufluss: FlowPotBrief[];
    abfluss: FlowPotBrief[];
    signale: Array<{ label: string; hint: string }>;
  } | null;
  /** Die letzten Tages-Briefe (neueste zuerst) — das Kurzzeit-Gedächtnis. */
  letzte_briefe: Array<{
    datum: string;
    kernaussage: string;
    szenarien: Array<{ name: string; p: number }>;
  }>;
  /** Verdichtete Wochen-Linien (sonntags erzeugt) — das Mittelfrist-Gedächtnis. */
  wochen_linien: Array<{ datum: string; kernaussage: string }>;
  /** Verdichtete Monats-Linien (am 1. erzeugt) — das Langzeit-Gedächtnis.
   *  Über die Zeit entsteht so der Blick auf Monate und Jahre. */
  monats_linien: Array<{ datum: string; kernaussage: string }>;
}

export interface MacroScenario {
  name: string;
  /** Wahrscheinlichkeit in %, 0–100. */
  p: number;
  story: string;
  gewinner: string[];
  verlierer: string[];
  fruehindikatoren: string[];
  kipp_signal: string;
}

export interface MacroBrief {
  kernaussage: string;
  entwicklung: string;
  tagesrauschen: string;
  szenarien: MacroScenario[];
}

/** Erlaubte Topf-Namen für Gewinner/Verlierer (deckungsgleich mit dem Radar). */
export const BRIEF_TOEPFE = [
  'Gold',
  'Silber',
  'Bitcoin',
  'Halbleiter / KI',
  'Technologie',
  'Rüstung',
  'Energie',
  'Banken / Finanzen',
  'Gesundheit',
  'Industrie',
  'Konsum (Basis)',
  'Konsum (zyklisch)',
  'Versorger',
  'China',
  'Indien',
  'Schwellenländer',
  'Südamerika',
  'Afrika',
  'Europa',
  'US-Anleihen',
  'US-Dollar',
] as const;

const SCHEMA_TEXT = `{
  "kernaussage": "1-2 Sätze — die große Linie jetzt",
  "entwicklung": "2-3 Sätze — was sich gegenüber deinen früheren Urteilen verfestigt, abgeschwächt oder gedreht hat",
  "tagesrauschen": "1-2 Sätze — was gerade laut ist, aber (noch) KEINE Trendwende belegt",
  "szenarien": [
    {
      "name": "Basis",
      "p": 55,
      "story": "2-3 Sätze: was in den nächsten 1-3 Monaten passiert und warum",
      "gewinner": ["Topf-Namen"],
      "verlierer": ["Topf-Namen"],
      "fruehindikatoren": ["woran erkenne ich früh, dass dieses Szenario eintritt"],
      "kipp_signal": "woran erkenne ich, dass dieses Szenario kippt"
    }
  ]
}`;

function fmtPp(v: number | null): string {
  if (v === null) return '?';
  return `${v > 0 ? '+' : ''}${v.toFixed(1)}`;
}

function fmtPot(p: FlowPotBrief): string {
  return `${p.label} (13W ${fmtPp(p.rs13)}pp, 26W ${fmtPp(p.rs26)}pp${p.hot ? ', heiß gelaufen' : ''})`;
}

function fmtVerlauf(rows: Array<{ datum: string; wert: number }>): string {
  if (rows.length === 0) return 'keine Daten';
  return rows.map((r) => `${r.datum}: ${Math.round(r.wert)}`).join(' → ');
}

export function buildMacroBriefMessages(d: MacroDossier): {
  system: string;
  user: string;
} {
  const system =
    'Du bist Makro-Analyst für einen langfristigen, ANTIZYKLISCHEN Privatanleger ' +
    '(Wochenchart, große Marktwellen, kauft in Angst, meidet Euphorie). Regeln:\n' +
    '1. LANGFRIST-LINIE vor Tagesnews: Bewerte die Entwicklung über Wochen und Monate. ' +
    'Einzelne Aussagen von Politikern oder einzelne Schlagzeilen zählen NUR, wenn sie einen ' +
    'mehrwöchigen Trend bestätigen oder brechen — sonst gehören sie unter "tagesrauschen".\n' +
    '2. GEDÄCHTNIS: Du siehst deine früheren Urteile. Sage ausdrücklich, was sich VERFESTIGT, ' +
    'ABGESCHWÄCHT oder GEDREHT hat. Keine 180-Grad-Wende ohne Beleg in den Zahlen oder einer ' +
    'mehrtägigen Nachrichtenlage.\n' +
    '3. Die gemessenen GELDFLÜSSE im Dossier sind der Anker — Nachrichten erklären sie. ' +
    'Widersprechen sich Story und Geldfluss, gewinnt der Geldfluss; benenne den Widerspruch offen.\n' +
    '4. Sprache: einfaches Deutsch, kurze Sätze, kein Fachjargon. KEIN Markdown, keine ' +
    'Sternchen (**) oder andere Formatierung — reiner Fließtext. KEINE persönliche ' +
    'Anlageberatung, keine Kaufempfehlung an eine Person — nur Markt-Szenarien mit Wahrscheinlichkeiten.\n' +
    '5. Antworte AUSSCHLIESSLICH mit gültigem JSON, exakt nach diesem Schema:\n' +
    SCHEMA_TEXT;

  const radarBlock = d.radar
    ? [
        `- Dreht gerade auf (Frühsignale): ${
          d.radar.frueh.length > 0 ? d.radar.frueh.map(fmtPot).join('; ') : 'keine'
        }`,
        `- Geld fließt rein: ${
          d.radar.zufluss.length > 0 ? d.radar.zufluss.map(fmtPot).join('; ') : 'keine'
        }`,
        `- Geld zieht ab: ${
          d.radar.abfluss.length > 0 ? d.radar.abfluss.map(fmtPot).join('; ') : 'keine'
        }`,
        `- Signalgeber: ${
          d.radar.signale.length > 0
            ? d.radar.signale.map((s) => `${s.label}: ${s.hint}`).join(' · ')
            : 'keine'
        }`,
      ].join('\n')
    : 'Radar heute ohne Daten.';

  const memory =
    d.letzte_briefe.length > 0
      ? d.letzte_briefe
          .map(
            (b) =>
              `[${b.datum}] ${b.kernaussage} — Szenarien damals: ${
                b.szenarien.map((s) => `${s.name} ${s.p} %`).join(', ') || 'keine'
              }`
          )
          .join('\n')
      : 'Noch keine früheren Briefe — dies ist der erste. Baue die Ausgangslinie auf.';

  const user = [
    `HEUTIGES DATUM: ${d.datum}`,
    '',
    '== ZAHLEN-DOSSIER (vom System gemessen — der Anker) ==',
    d.wetter
      ? `Markt-Wetter: ${d.wetter.phase} / ${d.wetter.stance} — ${d.wetter.headline} (Angst↔Euphorie: ${d.wetter.greed_level}/100)`
      : 'Markt-Wetter: keine Daten.',
    `Marktphase (SPY): ${d.markt_phase ?? 'unbekannt'} · Kapitulations-Score: ${
      d.kapitulation_score ?? '–'
    }`,
    `Fear&Greed-Verlauf (0=Panik, 100=Gier): ${fmtVerlauf(d.fg_verlauf)}`,
    `VIX-Verlauf: ${fmtVerlauf(d.vix_verlauf)}`,
    // #40 — Kreditmarkt: der Profi-Frühindikator (riecht Stress vor Aktien).
    d.kredit
      ? `Kreditmarkt (FRED): Hochzins-Aufschlag ${d.kredit.hy_spread_pct?.toFixed(2) ?? '–'} % (unter 3 = entspannt, über 4,5 = Stress) · Zinskurve 10J−2J ${d.kredit.zinskurve_10y2y?.toFixed(2) ?? '–'} pp (negativ = invertiert/Warnung)`
      : 'Kreditmarkt: keine Daten.',
    'GELDFLUSS-RADAR (Vorsprung vs. S&P 500 in Prozentpunkten, 13/26 Wochen):',
    radarBlock,
    '',
    '== DEINE FRÜHEREN URTEILE (Gedächtnis, neueste zuerst) ==',
    memory,
    '',
    '== WOCHEN-LINIEN (deine verdichteten Wochen-Urteile) ==',
    d.wochen_linien.length > 0
      ? d.wochen_linien.map((w) => `[Woche bis ${w.datum}] ${w.kernaussage}`).join('\n')
      : 'Noch keine — entstehen jeden Sonntag.',
    '',
    '== MONATS-LINIEN (deine verdichteten Monats-Urteile) ==',
    d.monats_linien.length > 0
      ? d.monats_linien.map((m) => `[Monat bis ${m.datum}] ${m.kernaussage}`).join('\n')
      : 'Noch keine — entstehen am Monatsersten.',
    '',
    '== AUFGABE ==',
    '1. Recherchiere die Weltlage der letzten 1-2 Wochen als TRENDS, nicht als Einzelmeldungen: ' +
      'Notenbanken/Zinsen, Inflation, Konjunktur USA/China/Europa, Geopolitik (Kriege/Waffenstillstände, ' +
      'Wahlen, Zölle/Handelspolitik), Gold/Öl/Dollar, Krypto.',
    '2. Erkläre, WARUM das Geld laut Radar so fließt — verbinde die Nachrichten-Trends mit den Zahlen. ' +
      'ANKER-REGEL: Behaupte KEINEN Geld-Zufluss in einen Topf, den das Radar nicht als Zufluss zeigt — ' +
      'widersprechen sich Nachrichten und Radar, benenne den Widerspruch offen (die Zahlen gewinnen).',
    '3. Vergleiche mit deinen früheren Urteilen: Was hat sich verfestigt, abgeschwächt, gedreht?',
    '4. Baue 2-3 Szenarien für die nächsten 1-3 Monate (p in %, Summe ungefähr 100). ' +
      `Gewinner/Verlierer NUR aus diesen Töpfen: ${BRIEF_TOEPFE.join(', ')}. ` +
      'Je Szenario: Frühindikatoren (woran erkenne ich es früh) und Kipp-Signal (woran erkenne ich, dass es kippt). ' +
      'WICHTIG: Ein Topf darf NUR als Gewinner oder Verlierer erscheinen, wenn die Begründung ' +
      'aus der SUMME der Entwicklung über Wochen kommt (Geldfluss-Trend, Wochen-/Monats-Linien, ' +
      'anhaltende Nachrichtenlage) — nie wegen einer einzelnen Meldung von heute oder gestern.',
    '5. Rechne POLITIK aktiv ein: Staatsbudgets und Förderprogramme (z. B. Raumfahrt, ' +
      'Verteidigung, Chips, Energie), Zölle, Regulierung, Wahlen. Wo eine politische ' +
      'Entscheidung ein Szenario treibt oder kippen kann, benenne sie — und nenne ' +
      'Zweitrunden-Profiteure (Zulieferer, Nachbarbranchen), nicht nur den offensichtlichen Sektor.',
    '6. Lege lautes Tagesgeschehen ohne Trendbeleg unter "tagesrauschen" ab.',
    '',
    'Antworte NUR mit dem JSON.',
  ].join('\n');

  return { system, user };
}

function asStrArray(v: unknown, max: number): string[] {
  if (!Array.isArray(v)) return [];
  return v.filter((x): x is string => typeof x === 'string' && x.trim().length > 0).slice(0, max);
}

export function parseMacroBriefResponse(raw: string): MacroBrief | null {
  const m = raw.match(/\{[\s\S]*\}/);
  if (!m) return null;
  let j: unknown;
  try {
    j = JSON.parse(m[0]);
  } catch {
    return null;
  }
  if (typeof j !== 'object' || j === null) return null;
  const o = j as Record<string, unknown>;

  const kernaussage = typeof o.kernaussage === 'string' ? o.kernaussage.trim() : '';
  if (!kernaussage) return null;

  const rawSz = Array.isArray(o.szenarien) ? o.szenarien : [];
  const szenarien: MacroScenario[] = [];
  for (const s of rawSz.slice(0, 4)) {
    if (typeof s !== 'object' || s === null) continue;
    const so = s as Record<string, unknown>;
    const name = typeof so.name === 'string' ? so.name.trim() : '';
    const story = typeof so.story === 'string' ? so.story.trim() : '';
    if (!name || !story) continue;
    const pNum = Number(so.p);
    szenarien.push({
      name,
      p: Number.isFinite(pNum) ? Math.max(0, Math.min(100, Math.round(pNum))) : 0,
      story,
      gewinner: asStrArray(so.gewinner, 6),
      verlierer: asStrArray(so.verlierer, 6),
      fruehindikatoren: asStrArray(so.fruehindikatoren, 6),
      kipp_signal: typeof so.kipp_signal === 'string' ? so.kipp_signal.trim() : '',
    });
  }
  if (szenarien.length === 0) return null;

  return {
    kernaussage,
    entwicklung: typeof o.entwicklung === 'string' ? o.entwicklung.trim() : '',
    tagesrauschen: typeof o.tagesrauschen === 'string' ? o.tagesrauschen.trim() : '',
    szenarien,
  };
}

// ── Rollups: Tage → Wochen-Linie → Monats-Linie ─────────────────────────────
// User-Direktive: „Die Zusammenfassung der letzten Wochen, Monate oder Jahre
// erkennt die Welle — nicht das Bündeln der Heute-Nachrichten." Die Rollups
// sind das Langzeit-Gedächtnis: rückblickend trennt die KI Rauschen von Trend
// und muss offen sagen, welche damaligen Szenarien eingetroffen oder
// widerlegt wurden (Ehrlichkeits-Prinzip wie bei der Strategie-Bilanz).

export interface RollupInput {
  datum: string;
  /** Die zu verdichtenden Urteile (älteste zuerst). */
  eintraege: Array<{
    datum: string;
    kernaussage: string;
    szenarien: Array<{ name: string; p: number }>;
  }>;
  /** Die vorige Linie derselben Ebene (Anschluss halten), falls vorhanden. */
  vorige_linie: string | null;
}

export function buildRollupMessages(
  scope: 'weekly' | 'monthly',
  input: RollupInput
): { system: string; user: string } {
  const ebene = scope === 'weekly' ? 'WOCHEN-Linie' : 'MONATS-Linie';
  const quelle = scope === 'weekly' ? 'Tages-Urteile dieser Woche' : 'Wochen-Linien dieses Monats';

  const system =
    'Du bist Makro-Analyst für einen langfristigen, antizyklischen Privatanleger. ' +
    `Deine Aufgabe: Verdichte die ${quelle} zu EINER ${ebene}.\n` +
    '1. Rückblickend trennen: Was war nur Rauschen (weglassen), was war echter Trend (behalten)?\n' +
    '2. Ehrlich prüfen: Welche damaligen Szenarien sind EINGETROFFEN, welche WIDERLEGT? Sag es offen.\n' +
    '3. Formuliere die Linie so, dass sie in Monaten noch verständlich ist (keine Tagesbezüge ohne Einordnung).\n' +
    '4. Einfaches Deutsch, keine persönliche Anlageberatung. KEIN Markdown/Sternchen — reiner Fließtext.\n' +
    '5. Antworte AUSSCHLIESSLICH mit gültigem JSON, exakt nach diesem Schema:\n' +
    SCHEMA_TEXT;

  const user = [
    `HEUTIGES DATUM: ${input.datum}`,
    '',
    `== ${quelle.toUpperCase()} (älteste zuerst) ==`,
    input.eintraege.length > 0
      ? input.eintraege
          .map(
            (e) =>
              `[${e.datum}] ${e.kernaussage} — Szenarien: ${
                e.szenarien.map((s) => `${s.name} ${s.p} %`).join(', ') || 'keine'
              }`
          )
          .join('\n')
      : 'Keine Einträge.',
    '',
    '== DEINE VORIGE LINIE (Anschluss halten) ==',
    input.vorige_linie ?? 'Noch keine — dies ist die erste.',
    '',
    'Verdichte jetzt: kernaussage = die Linie; entwicklung = was eingetroffen/widerlegt wurde; ' +
      'tagesrauschen = was sich rückblickend als Rauschen entpuppte; szenarien = 2-3 aktualisierte ' +
      'Szenarien aus dieser höheren Warte. Antworte NUR mit dem JSON.',
  ].join('\n');

  return { system, user };
}

/** Topf → liquider ETF (für „was könnte dazu passen"-Anzeige; kein Kaufsignal). */
export const TOPF_ETF: Record<string, string> = {
  Gold: 'GLD',
  Silber: 'SLV',
  Bitcoin: 'IBIT',
  'Halbleiter / KI': 'SMH',
  Technologie: 'XLK',
  Rüstung: 'ITA',
  Energie: 'XLE',
  'Banken / Finanzen': 'XLF',
  Gesundheit: 'XLV',
  Industrie: 'XLI',
  'Konsum (Basis)': 'XLP',
  'Konsum (zyklisch)': 'XLY',
  Versorger: 'XLU',
  China: 'FXI',
  Indien: 'INDA',
  Schwellenländer: 'EEM',
  Südamerika: 'ILF',
  Afrika: 'AFK',
  Europa: 'VGK',
  'US-Anleihen': 'TLT',
  'US-Dollar': 'UUP',
};
